import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowLeft, Car, Calendar, Shield, AlertCircle, CheckCircle2, XCircle, Key, FileText, Download, Image, X, Building, User, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ppf-auto-manager/artifacts/h7byux7s_UP-Logo-White.png";

const COVERAGE_NAMES = {
  // Individual items (new)
  loading_edge: 'Loading Edge', door_edge: 'Door Edge',
  front_bumper: 'Front Bumper', hood: 'Hood', side_mirrors: 'Side Mirrors', fenders: 'Fenders',
  premium_front_bumper: 'Front Bumper (Premium)', premium_hood: 'Hood (Premium)', 
  premium_side_mirrors: 'Side Mirrors (Premium)', premium_fenders: 'Fenders (Premium)',
  side_skirts: 'Side Skirts', a_pillars: 'A-Pillars', roof_section: 'Roof Section',
  whole_car: 'Whole Car', headlights: 'Headlights', windshield: 'Windshield',
  // Interior items (NEW)
  interior_screens: 'Screens', interior_full: 'Full Interior',
  // Legacy items
  small_parts: 'Small Parts (Loading Edge, Door Edge)',
  front_package: 'Front Package (Bumper, Hood, Mirrors, Fenders)',
  premium_package: 'Premium Package (Front + Skirts, A-Pillars, Roof)',
  complete_package: 'Complete Package (Whole Car)',
  extras_headlights: 'Headlights Protection',
  extras_windshield: 'Windshield Protection'
};

export default function ClientVINPage() {
  const [vin, setVin] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [warrantyData, setWarrantyData] = useState(null);
  const [error, setError] = useState('');
  const [downloadingCard, setDownloadingCard] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const validateVIN = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);

  const formatAccessCode = (value) => {
    let cleaned = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    if (cleaned.length === 5 && !cleaned.includes('-')) {
      cleaned = cleaned.slice(0, 5) + '-' + cleaned.slice(5);
    }
    return cleaned.slice(0, 10);
  };

  const handleSearch = async () => {
    if (vin.length !== 17) {
      setError('VIN must be exactly 17 characters');
      toast.error('VIN must be exactly 17 characters');
      return;
    }
    if (!accessCode || accessCode.length < 8) {
      setError('Please enter a valid access code');
      toast.error('Please enter a valid access code');
      return;
    }

    setLoading(true);
    setWarrantyData(null);
    setPhotos([]);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/public/warranty-lookup`, {
        vin: vin, access_code: accessCode
      });
      setWarrantyData(response.data);
      toast.success('Vehicle found!');
      
      // Load photos if available
      if (response.data.has_photos) {
        loadPhotos();
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No record found. Please check your VIN and Access Code.');
        toast.error('No record found');
      } else {
        setError('Error looking up warranty. Please try again.');
        toast.error('Error looking up warranty');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    setLoadingPhotos(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/photos`, {
        vin: vin, access_code: accessCode
      });
      setPhotos(response.data);
    } catch (err) {
      console.error('Error loading photos:', err);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const getCoverageList = (coverage) => {
    if (!coverage) return [];
    return Object.entries(coverage).filter(([_, v]) => v).map(([k]) => COVERAGE_NAMES[k] || k);
  };

  const downloadWarrantyCard = async () => {
    setDownloadingCard(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/warranty-card`, {
        vin: vin, access_code: accessCode
      });
      
      const data = response.data.data;
      const filename = response.data.filename || 'warranty_card';
      const base64Data = data.includes(',') ? data.split(',')[1] : data;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const mimeType = data.includes('application/pdf') ? 'application/pdf' : 'image/jpeg';
      const blob = new Blob([byteArray], { type: mimeType });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Warranty card downloaded!');
    } catch (err) {
      toast.error('Failed to download warranty card');
    } finally {
      setDownloadingCard(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-red-600/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="py-6 px-6 lg:px-12 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back to Home</span>
          </Link>
          <img src={LOGO_URL} alt="Unique Protection" className="h-8" />
        </header>

        <main className="flex-1 flex flex-col items-center px-6 py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-xl lg:max-w-5xl xl:max-w-7xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600/20 rounded-2xl mb-6">
                <Shield className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-['Chivo'] text-3xl sm:text-4xl font-black text-white mb-4">Warranty Lookup</h1>
              <p className="text-zinc-400">Enter your VIN and Access Code to check your PPF warranty</p>
            </div>

            <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8">
              <div className="space-y-4 lg:space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <Label className="text-zinc-400 text-sm flex items-center gap-2 mb-2">
                      <Car className="w-4 h-4" /> Vehicle Identification Number (VIN)
                    </Label>
                    <div className="relative">
                      <Input type="text" data-testid="vin-input" placeholder="Enter 17-character VIN"
                        value={vin} onChange={(e) => { setVin(validateVIN(e.target.value)); setError(''); }}
                        onKeyDown={handleKeyDown} maxLength={17}
                        className="h-12 text-base font-mono tracking-wider bg-zinc-950/50 border-white/10 focus:border-red-500/50 text-white placeholder:text-zinc-600 uppercase" />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-zinc-500">{vin.length}/17</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-zinc-400 text-sm flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4" /> Installation Access Code
                    </Label>
                    <Input type="text" data-testid="access-code-input" placeholder="Enter your access code"
                      value={accessCode} onChange={(e) => { setAccessCode(formatAccessCode(e.target.value)); setError(''); }}
                      onKeyDown={handleKeyDown} maxLength={10}
                      className="h-12 text-base font-mono tracking-wider bg-zinc-950/50 border-white/10 focus:border-red-500/50 text-white placeholder:text-zinc-600 uppercase" />
                    <p className="text-xs text-zinc-600 mt-1">Contact your installer if you don&apos;t have an access code</p>
                  </div>
                </div>

                <Button data-testid="warranty-search-btn" onClick={handleSearch}
                  disabled={loading || vin.length !== 17 || accessCode.length < 8}
                  className="w-full lg:w-auto lg:min-w-[200px] lg:mx-auto lg:flex h-12 bg-red-600 hover:bg-red-700 text-white font-bold shadow-[0_0_20px_rgba(220,38,38,0.3)] disabled:opacity-50 disabled:shadow-none mt-2">
                  {loading ? <span className="animate-pulse">Checking Warranty...</span> : <><Search className="w-5 h-5 mr-2" /> Check Warranty</>}
                </Button>
              </div>

              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-4 flex items-center gap-2 text-red-400 bg-red-500/10 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </motion.div>
              )}
            </div>

            <AnimatePresence>
              {warrantyData && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                  data-testid="warranty-results" className="space-y-4 lg:space-y-5">
                  
                  {/* Warranty Status - Compact bar on desktop, hero on mobile */}
                  <div className={`rounded-xl overflow-hidden ${warrantyData.warranty_active 
                    ? 'bg-emerald-500/10 border border-emerald-500/20' 
                    : 'bg-red-500/10 border border-red-500/20'}`}>
                    {/* Mobile: Hero style */}
                    <div className="lg:hidden p-6 text-center">
                      {warrantyData.warranty_active ? (
                        <>
                          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                          <h2 className="font-['Chivo'] text-2xl font-bold text-emerald-400 mb-2">Warranty Active</h2>
                          <p className="text-zinc-400">Protected until {formatDate(warrantyData.warranty_expiration)}</p>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                          <h2 className="font-['Chivo'] text-2xl font-bold text-red-400 mb-2">Warranty Expired</h2>
                          <p className="text-zinc-400">Expired on {formatDate(warrantyData.warranty_expiration)}</p>
                        </>
                      )}
                    </div>
                    {/* Desktop: Compact status bar */}
                    <div className="hidden lg:flex items-center justify-between px-6 py-4">
                      <div className="flex items-center gap-4">
                        {warrantyData.warranty_active ? (
                          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                        ) : (
                          <XCircle className="w-8 h-8 text-red-500" />
                        )}
                        <div>
                          <h2 className={`font-['Chivo'] text-xl font-bold ${warrantyData.warranty_active ? 'text-emerald-400' : 'text-red-400'}`}>
                            {warrantyData.warranty_active ? 'Warranty Active' : 'Warranty Expired'}
                          </h2>
                          <p className="text-zinc-400 text-sm">
                            {warrantyData.warranty_years} Year Coverage • {warrantyData.ppf_type?.replace('_', ' ')}{warrantyData.ppf_color && ` (${warrantyData.ppf_color})`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-zinc-500 text-xs uppercase tracking-wide">Valid Until</p>
                        <p className={`text-lg font-semibold ${warrantyData.warranty_active ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatDate(warrantyData.warranty_expiration)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Two Column Layout - Vehicle & PPF Details */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
                    {/* Vehicle Info */}
                    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 lg:px-5 lg:py-3 border-b border-white/5">
                        <h2 className="font-['Chivo'] text-base lg:text-lg font-bold text-white">Vehicle Information</h2>
                      </div>
                      <div className="p-4 lg:p-5 grid grid-cols-2 gap-3 lg:gap-4">
                        <div><p className="text-zinc-500 text-xs lg:text-sm">Vehicle</p><p className="text-white font-medium text-sm lg:text-base">{warrantyData.year} {warrantyData.brand} {warrantyData.model}</p></div>
                        <div><p className="text-zinc-500 text-xs lg:text-sm">Color</p><p className="text-white font-medium text-sm lg:text-base">{warrantyData.original_color}</p></div>
                        <div><p className="text-zinc-500 text-xs lg:text-sm">VIN</p><p className="text-white font-mono text-xs lg:text-sm">{warrantyData.vin}</p></div>
                        <div><p className="text-zinc-500 text-xs lg:text-sm">License Plate</p><p className="text-white font-medium text-sm lg:text-base">{warrantyData.license_plate}</p></div>
                      </div>
                    </div>

                    {/* PPF & Warranty Details */}
                    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 lg:px-5 lg:py-3 border-b border-white/5">
                        <h2 className="font-['Chivo'] text-base lg:text-lg font-bold text-white">PPF & Warranty</h2>
                      </div>
                      <div className="divide-y divide-white/5">
                        <div className="px-4 py-2.5 lg:px-5 lg:py-3 flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">PPF Type</span>
                          <span className="text-white font-medium text-sm">{warrantyData.ppf_type?.replace('_', ' ')}{warrantyData.ppf_color && ` (${warrantyData.ppf_color})`}</span>
                        </div>
                        <div className="px-4 py-2.5 lg:px-5 lg:py-3 flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">Installation Date</span>
                          <span className="text-white font-medium text-sm">{formatDate(warrantyData.install_date)}</span>
                        </div>
                        <div className="px-4 py-2.5 lg:px-5 lg:py-3 flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">Warranty Period</span>
                          <span className="text-white font-medium text-sm">{warrantyData.warranty_years} Years</span>
                        </div>
                        <div className="px-4 py-2.5 lg:px-5 lg:py-3 flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">Expiration</span>
                          <span className={`font-medium text-sm ${warrantyData.warranty_active ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatDate(warrantyData.warranty_expiration)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Installer Details Section */}
                  {(warrantyData.installer_name || warrantyData.installer_company) && (
                    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 lg:px-5 lg:py-3 border-b border-white/5 flex items-center gap-2">
                        <Building className="w-4 h-4 text-red-400" />
                        <h2 className="font-['Chivo'] text-base lg:text-lg font-bold text-white">Installer</h2>
                      </div>
                      <div className="p-4 lg:p-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {warrantyData.installer_company && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center flex-shrink-0">
                                <Building className="w-4 h-4 text-red-400" />
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Company</p>
                                <p className="text-white font-medium text-sm">{warrantyData.installer_company}</p>
                              </div>
                            </div>
                          )}
                          {warrantyData.installer_representative && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-blue-400" />
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Representative</p>
                                <p className="text-white font-medium text-sm">{warrantyData.installer_representative}</p>
                              </div>
                            </div>
                          )}
                          {warrantyData.installer_phone && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-emerald-600/10 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-4 h-4 text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Contact</p>
                                <a href={`tel:${warrantyData.installer_phone}`} className="text-white font-medium text-sm hover:text-red-400 transition-colors">
                                  {warrantyData.installer_phone}
                                </a>
                              </div>
                            </div>
                          )}
                          {warrantyData.installer_address && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-amber-600/10 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-4 h-4 text-amber-400" />
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Address</p>
                                <p className="text-white font-medium text-sm">{warrantyData.installer_address}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Warranty Service Status Section */}
                  <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 lg:px-5 lg:py-3 border-b border-white/5 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-400" />
                      <h2 className="font-['Chivo'] text-base lg:text-lg font-bold text-white">Warranty Service</h2>
                    </div>
                    <div className="p-4 lg:p-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          warrantyData.warranty_service 
                            ? 'bg-emerald-600/20 border border-emerald-500/30' 
                            : 'bg-amber-600/20 border border-amber-500/30'
                        }`}>
                          {warrantyData.warranty_service ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-400" />
                          )}
                        </div>
                        <div>
                          <p className={`font-semibold ${
                            warrantyData.warranty_service ? 'text-emerald-400' : 'text-amber-400'
                          }`}>
                            {warrantyData.warranty_service ? 'Service Completed' : 'Service Pending'}
                          </p>
                          {warrantyData.warranty_service && warrantyData.warranty_service_date && (
                            <p className="text-zinc-400 text-sm">
                              Inspected on {new Date(warrantyData.warranty_service_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                          )}
                          {!warrantyData.warranty_service && (
                            <p className="text-zinc-500 text-sm">
                              Post-installation inspection required for warranty validation
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coverage Areas - Compact grid on desktop */}
                  {getCoverageList(warrantyData.coverage_areas).length > 0 && (
                    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 lg:px-5 lg:py-3 border-b border-white/5">
                        <h2 className="font-['Chivo'] text-base lg:text-lg font-bold text-white">Coverage Areas</h2>
                      </div>
                      <div className="p-4 lg:p-5">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2 lg:gap-2.5">
                          {getCoverageList(warrantyData.coverage_areas).map((item, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-zinc-300 bg-zinc-800/40 rounded-md px-2.5 py-1.5 lg:px-3 lg:py-2">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                              <span className="text-xs lg:text-sm truncate">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Documents & Photos - Side by side on desktop */}
                  {(warrantyData.has_warranty_card || warrantyData.has_photos) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
                      {/* Warranty Card Download */}
                      {warrantyData.has_warranty_card && (
                        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl p-4 lg:p-5">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-5 h-5 text-red-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-white font-medium text-sm lg:text-base">Warranty Card</p>
                                <p className="text-zinc-500 text-xs lg:text-sm truncate">Download official document</p>
                              </div>
                            </div>
                            <Button onClick={downloadWarrantyCard} disabled={downloadingCard} size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white text-xs lg:text-sm flex-shrink-0">
                              {downloadingCard ? '...' : <><Download className="w-3.5 h-3.5 mr-1.5" /> Download</>}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Installation Photos */}
                      {warrantyData.has_photos && (
                        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 lg:px-5 lg:py-3 border-b border-white/5 flex items-center gap-2">
                            <Image className="w-4 h-4 text-red-400" />
                            <h2 className="font-['Chivo'] text-sm lg:text-base font-bold text-white">Installation Photos</h2>
                          </div>
                          <div className="p-4 lg:p-5">
                            {loadingPhotos ? (
                              <p className="text-zinc-500 text-center py-2 text-sm">Loading...</p>
                            ) : photos.length > 0 ? (
                              <div className="grid grid-cols-4 gap-2">
                                {photos.map((photo, i) => (
                                  <button key={i} onClick={() => setSelectedPhoto(photo.data)}
                                    className="aspect-square rounded-lg overflow-hidden bg-zinc-800 hover:ring-2 hover:ring-red-500 transition-all">
                                    <img src={photo.data} alt={`Installation ${i+1}`} className="w-full h-full object-cover" />
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-zinc-500 text-center py-2 text-sm">No photos</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-center text-zinc-500 text-xs lg:text-sm">
                    For warranty claims, please contact your installer.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {!warrantyData && !error && (
              <p className="text-center text-zinc-600 text-sm">Don't have an access code? Contact your PPF installer.</p>
            )}
          </motion.div>
        </main>

        <footer className="py-4 text-center">
          <p className="text-xs text-zinc-600">© 2026 Unique Protection. All rights reserved.</p>
        </footer>
      </div>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl p-2">
          <button onClick={() => setSelectedPhoto(null)} className="absolute top-2 right-2 p-2 bg-zinc-800 rounded-full hover:bg-zinc-700">
            <X className="w-5 h-5 text-white" />
          </button>
          {selectedPhoto && <img src={selectedPhoto} alt="Installation" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
